/**
 * Diálogos de alta y edición de cuentas.
 *
 * Vivían dentro de ProfilePage porque las cuentas se administraban en Perfil.
 * Ahora las cuentas viven en «Mi dinero» (que es donde una persona las busca),
 * así que los diálogos se mudan a un componente propio en vez de duplicarse.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney, errMsg } from "@/lib/format";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function AddAccountDialog({ open, onOpenChange, onCreated, defaultCurrency = "USD", effectiveTier }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("checking");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [balance, setBalance] = useState("");
  const [color, setColor] = useState("#8B5CF6");
  const [bankCountry, setBankCountry] = useState("");
  const [isShared, setIsShared] = useState(false);
  const colors = ["#8B5CF6", "#38BDF8", "#10B981", "#F59E0B", "#F43F5E", "#EC4899"];
  const familyEligible = effectiveTier === "family" || effectiveTier === "lifetime";
  const showBankCountry = ["checking", "savings", "credit"].includes(type);
  const isUSDT = currency === "USDT";

  // Keep default currency in sync if user prop changes
  useEffect(() => { setCurrency(defaultCurrency); }, [defaultCurrency, open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounts", {
        name, type, balance: Number(balance || 0), color, currency,
        bank_country: bankCountry || null,
        is_shared: !!isShared,
      });
      toast.success("Cuenta creada");
      setName(""); setBalance(""); setBankCountry(""); setIsShared(false);
      onCreated?.(); onOpenChange(false);
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md" data-testid="add-account-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Nueva cuenta</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">Define moneda, tipo y país del banco si aplica.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nombre (ej: Banco Popular Ahorros)" data-testid="account-name"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm"/>
          <div className="grid grid-cols-2 gap-3">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="account-type" className="bg-white/5 border-white/10 text-white h-auto px-3 py-3 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#15141b] border-white/10 text-white">
                <SelectItem value="checking">Corriente</SelectItem>
                <SelectItem value="savings">Ahorros</SelectItem>
                <SelectItem value="credit">Crédito</SelectItem>
                <SelectItem value="cash">Efectivo</SelectItem>
                <SelectItem value="investment">Inversión</SelectItem>
              </SelectContent>
            </Select>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="account-currency" className="bg-white/5 border-white/10 text-white h-auto px-3 py-3 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#15141b] border-white/10 text-white max-h-72">
                {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isUSDT && (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-2.5 flex items-start gap-2" data-testid="usdt-hint">
              <Coins className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0"/>
              <div className="text-[12px] text-emerald-200 leading-relaxed">
                Cuenta en <b>USDT</b> (stablecoin). Cuando llegues a finanzas → tasas, vas a poder configurar el precio de venta para que tu patrimonio se calcule correcto al convertir a tu moneda local.
              </div>
            </div>
          )}
          {showBankCountry && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">
                País del banco <span className="text-slate-600 font-normal normal-case">(opcional, marca solo si es internacional)</span>
              </div>
              <Select value={bankCountry || "__none__"} onValueChange={(v) => setBankCountry(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="account-bank-country" className="bg-white/5 border-white/10 text-white h-auto px-3 py-3 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#15141b] border-white/10 text-white max-h-72">
                  <SelectItem value="__none__">— local / sin especificar —</SelectItem>
                  {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{`${c.code} · ${c.name}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <input type="number" value={balance} onChange={(e) => setBalance(e.target.value)} placeholder="Saldo inicial" data-testid="account-balance"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm"/>
          {familyEligible && (
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-violet-500/10 border border-violet-500/20" data-testid="account-shared-toggle">
              <input type="checkbox" checked={isShared} onChange={(e) => setIsShared(e.target.checked)}
                className="w-4 h-4 accent-violet-500"/>
              <div>
                <div className="text-sm font-semibold text-violet-200">Compartir con mi hogar</div>
                <div className="text-[11px] text-violet-300/70">Los miembros de tu familia verán y podrán usar esta cuenta.</div>
              </div>
            </label>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Color</div>
            <div className="flex gap-2">
              {colors.map(c => (
                <button type="button" key={c} onClick={() => setColor(c)} className={`w-8 h-8 rounded-full transition-all ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#15141b]" : ""}`} style={{ background: c }}/>
              ))}
            </div>
          </div>
          <button type="submit" data-testid="account-submit" className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl">Crear cuenta</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditAccountDialog({ account, onClose, onSaved, effectiveTier }) {
  const [form, setForm] = useState({});
  // Motivo del ajuste de saldo. Obligatorio si el saldo cambia: una corrección
  // sin explicación es indistinguible de un error de la app seis meses después.
  const [adjustReason, setAdjustReason] = useState("");
  const familyEligible = effectiveTier === "family" || effectiveTier === "lifetime";
  useEffect(() => {
    if (!account) return;
    setForm({
      name: account.name || "",
      type: account.type || "checking",
      currency: account.currency || "USD",
      bank_country: account.bank_country || "",
      is_shared: !!account.is_shared,
      color: account.color || "#8B5CF6",
      balance: String(account.balance ?? 0),
    });
  }, [account]);
  if (!account) return null;
  const showBankCountry = ["checking", "savings", "credit"].includes(form.type);
  const balanceChanged = Number(form.balance) !== Number(account.balance ?? 0);
  const save = async (e) => {
    e.preventDefault();
    if (form.balance === "" || Number.isNaN(Number(form.balance))) {
      return toast.error("El saldo debe ser un número");
    }
    if (balanceChanged && !adjustReason.trim()) {
      return toast.error("Explica por qué cambia el saldo (queda registrado)");
    }
    try {
      await api.patch(`/accounts/${account.id}`, {
        name: form.name,
        type: form.type,
        currency: form.currency,
        color: form.color,
        bank_country: form.bank_country || null,
        is_shared: !!form.is_shared,
      });
      // El saldo NO viaja en el PATCH. Escribirlo directo rompía el libro sin
      // dejar rastro: el saldo dejaba de ser la suma de sus movimientos y nada
      // podía detectarlo. Ahora la diferencia se registra como un movimiento
      // visible, con motivo — y sólo si de verdad la tocaste (mandarlo siempre
      // revertiría cualquier transacción hecha mientras el diálogo está abierto).
      if (balanceChanged) {
        await api.post(`/accounts/${account.id}/adjust-balance`, {
          new_balance: Number(form.balance),
          reason: adjustReason.trim(),
        });
      }
      toast.success(balanceChanged ? "Cuenta actualizada y saldo ajustado" : "Cuenta actualizada");
      setAdjustReason("");
      onSaved?.();
      onClose();
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const colors = ["#8B5CF6", "#38BDF8", "#10B981", "#F59E0B", "#F43F5E", "#EC4899"];

  return (
    <Dialog open={!!account} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md" data-testid="edit-account-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Editar cuenta</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">Corrige el saldo, la moneda, el país del banco o el modo compartido.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} required placeholder="Nombre" data-testid="edit-account-name"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm"/>
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.type} onValueChange={(v) => setForm(f => ({...f, type: v}))}>
              <SelectTrigger data-testid="edit-account-type" className="bg-white/5 border-white/10 text-white h-auto px-3 py-3 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#15141b] border-white/10 text-white">
                <SelectItem value="checking">Corriente</SelectItem>
                <SelectItem value="savings">Ahorros</SelectItem>
                <SelectItem value="credit">Crédito</SelectItem>
                <SelectItem value="cash">Efectivo</SelectItem>
                <SelectItem value="investment">Inversión</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.currency} onValueChange={(v) => setForm(f => ({...f, currency: v}))}>
              <SelectTrigger data-testid="edit-account-currency" className="bg-white/5 border-white/10 text-white h-auto px-3 py-3 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#15141b] border-white/10 text-white max-h-72">
                {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {showBankCountry && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">País del banco (opcional)</div>
              <Select value={form.bank_country || "__none__"} onValueChange={(v) => setForm(f => ({...f, bank_country: v === "__none__" ? "" : v}))}>
                <SelectTrigger data-testid="edit-account-bank-country" className="bg-white/5 border-white/10 text-white h-auto px-3 py-3 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#15141b] border-white/10 text-white max-h-72">
                  <SelectItem value="__none__">— local / sin especificar —</SelectItem>
                  {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{`${c.code} · ${c.name}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">Saldo actual</div>
            <input type="number" step="0.01" value={form.balance ?? ""} data-testid="edit-account-balance"
              onChange={(e) => setForm(f => ({...f, balance: e.target.value}))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-lg font-bold"/>
            {balanceChanged && (
              <div className="mt-2 space-y-1.5" data-testid="edit-account-balance-warning">
                <div className="text-[11px] text-amber-300/80 leading-relaxed">
                  Se registrará un ajuste de {fmtMoney(Math.abs(Number(form.balance) - Number(account.balance ?? 0)), account.currency)} en
                  tu historial. No cuenta como ingreso ni como gasto del mes.
                </div>
                <input
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="¿Por qué? Ej: intereses que el banco pagó"
                  data-testid="edit-account-adjust-reason"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
              </div>
            )}
          </div>
          {familyEligible && (
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-violet-500/10 border border-violet-500/20" data-testid="edit-account-shared-toggle">
              <input type="checkbox" checked={!!form.is_shared} onChange={(e) => setForm(f => ({...f, is_shared: e.target.checked}))}
                className="w-4 h-4 accent-violet-500"/>
              <div>
                <div className="text-sm font-semibold text-violet-200">Compartir con mi hogar</div>
                <div className="text-[11px] text-violet-300/70">Visible y usable por todos los miembros de tu familia.</div>
              </div>
            </label>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Color</div>
            <div className="flex gap-2">
              {colors.map(c => (
                <button type="button" key={c} onClick={() => setForm(f => ({...f, color: c}))} className={`w-8 h-8 rounded-full transition-all ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#15141b]" : ""}`} style={{ background: c }}/>
              ))}
            </div>
          </div>
          <button type="submit" data-testid="edit-account-submit" className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl">Guardar cambios</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

